import React, { useEffect, useState } from 'react';
import { CheckCircleOutlined, DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Input, Popconfirm, Row, Select, Space, Table, Tag, Tooltip } from 'antd';
import dayjs from "dayjs";
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ContentHeader } from '../../components';
import { menuHelper } from '../../helpers/account';
import { Links } from '../../helpers/Links';
import { DotKiemKeTaiSan, TrangThaiDotKiemKe } from '../../models/taisan/DotKiemKeTaiSan';
import { timKiemDSDotKiemKe, xoaDotKiemKe } from '../../services/dotKiemKeTaiSan';
import { dateCommon } from '../../helpers/common';
import { useSelector } from 'react-redux';

const { RangePicker } = DatePicker;

const DanhSachDotKiemKe = () => {
    const navigate = useNavigate();
    const user = useSelector((state: any) => state.auth.currentUser);
    const menu: any = menuHelper.checkMultiPermission([Links.TaiSan.ChiTietDotKiemKe]);
    const [loading, setLoading] = useState<boolean>(false);
    const [dsDotKiemKe, setDsDotKiemKe] = useState<DotKiemKeTaiSan[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);

    const [tuKhoa, setTuKhoa] = useState<string>("");
    const [trangThai, setTrangThai] = useState<number | undefined>();
    const [tuNgay, setTuNgay] = useState<string | undefined>();
    const [denNgay, setDenNgay] = useState<string | undefined>();

    const trangThaiOptions = [
        { value: TrangThaiDotKiemKe.KhoiTao, label: 'Khởi tạo' },
        { value: TrangThaiDotKiemKe.ThuHoi, label: 'Thu hồi' },
        { value: TrangThaiDotKiemKe.TrinhPhoCVP, label: 'Trình Phó CVP' },
        { value: TrangThaiDotKiemKe.PhoCVPTraLai, label: 'Phó CVP trả lại' },
        { value: TrangThaiDotKiemKe.TrinhCVP, label: 'Trình CVP' },
        { value: TrangThaiDotKiemKe.CVPTraLai, label: 'CVP trả lại' },
        { value: TrangThaiDotKiemKe.TrinhPhoGiamDoc, label: 'Trình Phó Giám đốc' },
        { value: TrangThaiDotKiemKe.PhoGiamDocTraLai, label: 'Phó Giám đốc trả lại' },
        { value: TrangThaiDotKiemKe.TrinhGiamDoc, label: 'Trình Giám đốc' },
        { value: TrangThaiDotKiemKe.GiamDocTraLai, label: 'Giám đốc trả lại' },
        { value: TrangThaiDotKiemKe.PheDuyet, label: 'Đã phê duyệt' },
        { value: TrangThaiDotKiemKe.DangKiemKe, label: 'Đang kiểm kê' },
        { value: TrangThaiDotKiemKe.HoanThanh, label: 'Hoàn thành' },
        { value: TrangThaiDotKiemKe.DaDong, label: 'Đã đóng' },
    ];

    const getTrangThaiTag = (trangThai: number) => {
        switch (trangThai) {
            case TrangThaiDotKiemKe.KhoiTao:
                return <Tag color="default">Khởi tạo</Tag>;
            case TrangThaiDotKiemKe.ThuHoi:
                return <Tag color="red">Thu hồi</Tag>;
            case TrangThaiDotKiemKe.TrinhPhoCVP:
            case TrangThaiDotKiemKe.TrinhCVP:
            case TrangThaiDotKiemKe.TrinhPhoGiamDoc:
            case TrangThaiDotKiemKe.TrinhGiamDoc:
                return <Tag color="blue">Đang trình duyệt</Tag>;
            case TrangThaiDotKiemKe.PhoCVPTraLai:
            case TrangThaiDotKiemKe.CVPTraLai:
            case TrangThaiDotKiemKe.PhoGiamDocTraLai:
            case TrangThaiDotKiemKe.GiamDocTraLai:
                return <Tag color="orange">Bị trả lại</Tag>;
            case TrangThaiDotKiemKe.PheDuyet:
                return <Tag color="green">Đã duyệt</Tag>;
            case TrangThaiDotKiemKe.DangKiemKe:
                return <Tag color="cyan">Đang kiểm kê</Tag>;
            case TrangThaiDotKiemKe.HoanThanh:
                return <Tag color="purple">Hoàn thành</Tag>;
            case TrangThaiDotKiemKe.DaDong:
                return <Tag color="magenta">Đã đóng</Tag>;
            default:
                return <Tag>Không xác định</Tag>;
        }
    };

    const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

    React.useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const columns = [
        {
            title: 'STT',
            dataIndex: 'stt',
            key: 'stt',
            width: 50,
            render: (_: any, __: any, index: number) => (currentPage - 1) * pageSize + index + 1,
            responsive: ['md'] as const,
        },
        {
            title: 'Mã đợt',
            dataIndex: 'maDotKiemKe',
            key: 'maDotKiemKe',
            width: isMobile ? 100 : 150,
        },
        {
            title: 'Tên đợt kiểm kê',
            dataIndex: 'tenDotKiemKe',
            key: 'tenDotKiemKe',
            width: isMobile ? 200 : 300,
        },
        {
            title: 'Ngày bắt đầu',
            dataIndex: 'ngayBatDau',
            key: 'ngayBatDau',
            width: isMobile ? 85 : 120,
            render: (date: Date) => date ? dateCommon.formatDate(date) : '-',
            responsive: ['md'] as const,
        },
        {
            title: 'Ngày kết thúc',
            dataIndex: 'ngayKetThuc',
            key: 'ngayKetThuc',
            width: isMobile ? 85 : 120,
            render: (date: Date) => date ? dateCommon.formatDate(date) : '-',
            responsive: ['md'] as const,
        },
        {
            title: 'Người khởi tạo',
            dataIndex: 'tenNVKhoiTao',
            key: 'tenNVKhoiTao',
            width: isMobile ? 130 : 180,
            render: (text: string) => text || '-',
            responsive: ['lg'] as const,
        },
        {
            title: 'Trạng thái',
            dataIndex: 'trangThai',
            key: 'trangThai',
            width: 110,
            render: (trangThai: number) => getTrangThaiTag(trangThai),
        },
        {
            title: 'Thao tác',
            key: 'action',
            width: isMobile ? 80 : 230,
            align: "center" as const,
            fixed: 'right' as 'right',
            render: (_: any, record: DotKiemKeTaiSan) => {
                const isCreator = record.idNVKhoiTao === user?.id;
                const canKiemKe = record.trangThai >= TrangThaiDotKiemKe.PheDuyet && (!record.idsDSNguoiPhuTrach || record.idsDSNguoiPhuTrach.length === 0 || record.idsDSNguoiPhuTrach.includes(user?.idNhanVien) || record.idsDSNguoiPhuTrach.includes(user?.id));

                return (
                    <Space size={isMobile ? "small" : "middle"} direction={isMobile ? "vertical" : "horizontal"}>
                        {canKiemKe && (
                            <Link to={`${Links.TaiSan.KiemKeTaiSan}?idDotKiemKe=${record.id}`}>
                                <Button type="primary" size="small" icon={<CheckCircleOutlined />}>
                                    {!isMobile && 'Kiểm kê'}
                                </Button>
                            </Link>
                        )}
                        {menu[Links.TaiSan.ChiTietDotKiemKe] && isCreator && (
                            <Link to={`${Links.TaiSan.ChiTietDotKiemKe}?id=${record.id}`}>
                                <Button size={"small"} icon={<EditOutlined />}>
                                    {!isMobile && 'Sửa'}
                                </Button>
                            </Link>
                        )}
                        {menu[Links.TaiSan.ChiTietDotKiemKe] && !isCreator && (
                            <Link to={`${Links.TaiSan.ChiTietDotKiemKe}?id=${record.id}`}>
                                <Button size={"small"} icon={<EyeOutlined />}>
                                    {!isMobile && 'Xem'}
                                </Button>
                            </Link>
                        )}
                        {isCreator && (
                            <Popconfirm
                                title="Bạn có chắc chắn muốn xóa đợt kiểm kê này?"
                                onConfirm={() => handleXoa(record.id!)}
                                okText="Có"
                                cancelText="Không"
                            >
                                <Button type="primary" danger size="small" icon={<DeleteOutlined />}>
                                    {!isMobile && 'Xóa'}
                                </Button>
                            </Popconfirm>
                        )}
                    </Space>
                );
            },
        },
    ];

    const loadDanhSach = async () => {
        try {
            setLoading(true);
            const result = await timKiemDSDotKiemKe(
                {
                    tuKhoa,
                    trangThai,
                    tuNgay,
                    denNgay,
                },
                (currentPage - 1) * pageSize,
                pageSize
            );
            setDsDotKiemKe(result.data);
            setTotal(result.recordsTotal);
        } catch (error: any) {
            toast.error('Lỗi khi tải danh sách đợt kiểm kê: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleTimKiem = () => {
        setCurrentPage(1);
        loadDanhSach();
    };

    const handleXoa = async (id: number) => {
        try {
            await xoaDotKiemKe(id);
            toast.success('Xóa đợt kiểm kê thành công');
            loadDanhSach();
        } catch (error: any) {
            toast.error('Lỗi khi xóa đợt kiểm kê: ' + error.message);
        }
    };

    const handleDateRangeChange = (dates: any) => {
        if (dates) {
            setTuNgay(dates[0] ? dayjs(dates[0]).format("YYYY-MM-DD") : undefined);
            setDenNgay(dates[1] ? dayjs(dates[1]).format("YYYY-MM-DD") : undefined);
        } else {
            setTuNgay(undefined);
            setDenNgay(undefined);
        }
    };

    useEffect(() => {
        loadDanhSach();
    }, [currentPage, pageSize]);

    return (
        <section>
            <ContentHeader title="Quản lý đợt kiểm kê tài sản" />
            <Row className="form-group" gutter={[8, 8]}>
                <Col xs={24} sm={12} md={8}>
                    <Input
                        placeholder="Tìm kiếm..."
                        value={tuKhoa}
                        onChange={(e) => setTuKhoa(e.target.value)}
                        onPressEnter={handleTimKiem}
                        prefix={<SearchOutlined />}
                    />
                </Col>
                <Col xs={24} sm={12} md={5}>
                    <Select
                        allowClear
                        placeholder="Trạng thái"
                        style={{ width: '100%' }}
                        options={trangThaiOptions}
                        value={trangThai}
                        onChange={(value) => setTrangThai(value)}
                    />
                </Col>
                <Col xs={24} sm={24} md={6}>
                    <RangePicker
                        style={{ width: '100%' }}
                        placeholder={['Từ ngày', 'Đến ngày']}
                        format="DD/MM/YYYY"
                        onChange={handleDateRangeChange}
                    />
                </Col>
                <Col xs={24} sm={12} md={5}>
                    <Space style={{ width: '100%', justifyContent: isMobile ? 'space-between' : 'flex-start' }}>
                        <Tooltip title="Tìm kiếm">
                            <Button type="primary"
                                icon={<SearchOutlined />}
                                onClick={handleTimKiem}
                                style={{ width: isMobile ? '46vw' : 'auto' }}
                            >
                                Tìm kiếm
                            </Button>
                        </Tooltip>
                        {menu[Links.TaiSan.ChiTietDotKiemKe] && (
                            <Tooltip title="Thêm mới">
                                <Link className="hrm-btn hrm-btn-warning" to={Links.TaiSan.ChiTietDotKiemKe}
                                    style={{ width: isMobile ? '46vw' : 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <span style={{ alignItems: 'center', display: 'flex', gap: '4px' }}>
                                        <PlusOutlined />
                                        Thêm mới
                                    </span>
                                </Link>
                            </Tooltip>
                        )}
                    </Space>
                </Col>
            </Row>
            <Row>
                <Col span={24}>
                    <div className="card">
                        <div className="card-body">
                            <Table
                                columns={columns}
                                dataSource={dsDotKiemKe}
                                loading={loading}
                                rowKey="id"
                                scroll={{ x: isMobile ? 800 : 1400 }}
                                pagination={{
                                    current: currentPage,
                                    pageSize: pageSize,
                                    total: total,
                                    showSizeChanger: true,
                                    showTotal: (total) => `Tổng số: ${total}`,
                                    onChange: (page, size) => {
                                        setCurrentPage(page);
                                        setPageSize(size);
                                    },
                                }}
                            />
                        </div>
                    </div>
                </Col>
            </Row>
        </section>
    );
};

export default DanhSachDotKiemKe;
